/* Tax arithmetic shared by the dish editor and the kiosk — the same rules as
   every bill and the buffet ticket (backend apps/masters/dish_tax.py and the
   buffet controller's _ticket_taxes): non-compound percentages on the net,
   compound ones on the running total, flat amounts as they are. Rows are what
   /taxes/applicable/ and a menu item's `tax.labels` carry:
   { id, name, rate, rate_type: 'PERCENT' | 'FLAT', is_compound }. */

const r2 = (n) => Math.round((Number(n) + 1e-9) * 100) / 100;

/** Tax ON a net price → { total, parts }. */
export function addTax(net, rows) {
  const base = Number(net) || 0;
  let total = base;
  const parts = [];
  (rows || []).forEach((tx) => {
    const amt = tx.rate_type === 'FLAT'
      ? Number(tx.rate) || 0
      : ((tx.is_compound ? total : base) * (Number(tx.rate) || 0)) / 100;
    const r = r2(amt);
    parts.push({ id: tx.id, name: tx.name, amount: r });
    total += r;
  });
  return { total: r2(total), parts };
}

/** One typed price → { net, gross, tax, parts }, whichever end it was typed at.
    Backing tax out of a price with the tax inside is not "divide by 1.15" once
    there are several taxes, some compound, some flat — but addTax is affine in
    the net (gross = a × net + b), so two probes read a and b off it and invert
    it exactly for any mix, with no second copy of the rules to drift. The guest
    pays exactly the typed figure; the net absorbs the rounding, floored at zero
    for a price smaller than the flat taxes said to be inside it. */
export function splitPrice(price, rows, inclusive) {
  const p = Number(price) || 0;
  if (!p || !(rows && rows.length)) return { net: p, gross: p, tax: 0, parts: [] };
  if (!inclusive) {
    const { total, parts } = addTax(p, rows);
    return { net: p, gross: total, tax: r2(total - p), parts };
  }
  const b = addTax(0, rows).total;
  const a = (addTax(1000, rows).total - b) / 1000;
  const net = Math.max(0, a > 0 ? (p - b) / a : p - b);
  const { parts } = addTax(net, rows);
  const tax = r2(parts.reduce((s, x) => s + x.amount, 0));
  return { net: Math.max(0, r2(p - tax)), gross: p, tax, parts };
}

/* A menu as the ordering endpoints shape it, with every price turned into
   what the guest pays. The server sends each dish's prices as typed in the
   editor, with the taxes that ride on them and whether the typed figure
   already has the tax inside (`item.tax.inclusive`); this converts the lot
   once, at the door, so no screen has to know which way round a dish was
   priced. The net stays beside each one as `price_net`, because that is what
   an order line carries: the bill adds the tax back on top of the net, so
   the guest pays exactly the figure they were shown. Shared by the kiosk,
   the guest QR menu and the staff order screen — one rule, three doors. */
export function withGuestPrices(data) {
  if (!data || !Array.isArray(data.sections)) return data;
  return {
    ...data,
    sections: data.sections.map((sec) => ({
      ...sec,
      items: (sec.items || []).map((it) => {
        const rows = (it.tax && it.tax.labels) || [];
        const inclusive = !!(it.tax && it.tax.inclusive);
        const paid = (p) => splitPrice(Number(p) || 0, rows, inclusive);
        return {
          ...it,
          variants: (it.variants || []).map((v) => {
            const s = v.is_free ? { net: 0, gross: 0 } : paid(v.price);
            return {
              ...v,
              price: s.gross,
              price_net: s.net,
              addons: (v.addons || []).map((a) => {
                const x = paid(a.price);
                return { ...a, price: x.gross, price_net: x.net };
              }),
            };
          }),
        };
      }),
    })),
  };
}

/** "VAT 15%", or "VAT 15% + Service 10%" — the taxes named the way a guest reads them. */
export function taxNames(rows) {
  return (rows || [])
    .map((tx) => (tx.rate_type === 'FLAT' ? tx.name : `${tx.name} ${Number(tx.rate)}%`))
    .join(' + ');
}
